// src/components/AnnouncementPopup.js
// Site-wide announcement modal — London Smoke & Cure wholesale design language

import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, collection, getDocs, query, where, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useReducedMotion } from '../hooks/useReducedMotion';
import {
    trackAnnouncementView,
    trackAnnouncementClick,
    trackAnnouncementDismissal,
    getABTestVariant,
    matchesTargeting,
} from '../services/announcementAnalytics';
import {
    markAnnouncementDismissed,
    markAnnouncementPopupShown,
    shouldShowAnnouncementPopup,
    announcementShowsPopup,
    normalizeAnnouncementDisplayFrequency,
} from '../utils/announcementDisplayStorage';
import {
    isProductSplashAnnouncement,
    getProductSplashCampaignId,
    buildProductSplashCtaPath,
    buildProductSplashSeenMerge,
    writeProductSplashSeenLocal,
} from '../utils/productSplashContract';
import {
    loadShopProductRowForSplash,
    buildSplashAddToCartLine,
    SPLASH_ADD_FAIL_COPY,
} from '../utils/productSplashCart';
import { resolvePricingKeyFromUserData } from '../services/pricingTierService';
import AnnouncementPopupCard from './AnnouncementPopupCard';
import './AnnouncementPopup.css';

const db = getFirestore();

export default function AnnouncementPopup() {
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [variant, setVariant] = useState('A');
    const [userData, setUserData] = useState(null);
    const viewStartTime = useRef(null);
    const checkInFlightRef = useRef(false);
    const shownPopupIdRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { addItemsToCart, customerPricingKey } = useCart();
    const reducedMotion = useReducedMotion();
    const splashPersistInFlightRef = useRef(false);
    const splashSeenRef = useRef({});
    const [addBusy, setAddBusy] = useState(false);

    useEffect(() => {
        splashSeenRef.current = {};
        shownPopupIdRef.current = null;
        if (!user?.uid) {
            setUserData(null);
            return;
        }
        fetchUserData();
    }, [user?.uid]);

    useEffect(() => {
        if (!userData?.uid) return;
        checkAndShowAnnouncement();
    }, [userData?.uid, location.pathname]);

    const fetchUserData = async () => {
        if (!user?.uid) return;
        try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
                setUserData({ uid: user.uid, ...userDoc.data() });
            } else {
                setUserData({ uid: user.uid, role: 'Customer', Pricing: 'a' });
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            setUserData({ uid: user.uid, role: 'Customer', Pricing: 'a' });
        }
    };

    const checkAndShowAnnouncement = async () => {
        if (checkInFlightRef.current || shownPopupIdRef.current) return;
        checkInFlightRef.current = true;

        try {
            const settingsDoc = await getDoc(doc(db, 'system_settings', 'announcements'));
            if (settingsDoc.exists() && settingsDoc.data().enabled === false) {
                return;
            }

            const now = new Date().toISOString().split('T')[0];
            const snapshot = await getDocs(query(collection(db, 'announcements'), where('active', '==', true)));
            const splashOptions = {
                uid: userData.uid,
                firestoreReady: true,
                firestoreSeen: {
                    ...(userData.productSplashSeen || {}),
                    ...splashSeenRef.current,
                },
                isoDate: now,
                pathname: location.pathname,
            };

            const announcements = [];
            snapshot.forEach((d) => {
                const data = { id: d.id, ...d.data() };

                if (!shouldShowAnnouncementPopup(data, splashOptions)) return;
                if (data.expiryDate && data.expiryDate < now) return;

                if (!announcementShowsPopup(data)) return;

                if (!matchesTargeting(data, userData)) return;

                announcements.push(data);
            });

            if (announcements.length === 0) return;

            announcements.sort((a, b) => (b.priority || 1) - (a.priority || 1));
            const announcement = announcements[0];

            // Splash: do not session-mark on first paint. A crash before dismiss can re-show.
            if (!isProductSplashAnnouncement(announcement)) {
                markAnnouncementPopupShown(announcement.id);
            }
            shownPopupIdRef.current = announcement.id;

            const testVariant =
                announcement.abTesting?.enabled ? getABTestVariant(announcement.id, userData.uid) : 'A';

            setVariant(testVariant);
            setCurrentAnnouncement(announcement);
            viewStartTime.current = Date.now();

            trackAnnouncementView(
                announcement.id,
                userData.uid,
                userData.role,
                resolvePricingKeyFromUserData(userData),
                testVariant,
            ).catch((err) => console.warn('[AnnouncementPopup] view track failed', err));

            setTimeout(() => setIsVisible(true), 500);
        } catch (error) {
            console.error('Error fetching announcements:', error);
        } finally {
            checkInFlightRef.current = false;
        }
    };

    const persistProductSplashSeen = async (announcement) => {
        const uid = user?.uid || userData?.uid;
        const campaignId = getProductSplashCampaignId(announcement);
        if (!uid || !campaignId) return;
        splashSeenRef.current = {
            ...splashSeenRef.current,
            [campaignId]: { seenAt: true },
        };
        writeProductSplashSeenLocal(uid, campaignId, new Date().toISOString());
        setUserData((prev) => {
            if (!prev || prev.uid !== uid) return prev;
            return {
                ...prev,
                productSplashSeen: {
                    ...(prev.productSplashSeen || {}),
                    [campaignId]: { seenAt: true },
                },
            };
        });
        if (splashPersistInFlightRef.current) return;
        splashPersistInFlightRef.current = true;
        const merge = buildProductSplashSeenMerge(campaignId, serverTimestamp());
        try {
            if (merge) {
                await setDoc(doc(db, 'users', uid), merge, { merge: true });
            }
        } catch (error) {
            console.warn('[AnnouncementPopup] product splash seen write failed', error);
        } finally {
            splashPersistInFlightRef.current = false;
        }
    };

    const handleClose = async () => {
        if (!currentAnnouncement) return;

        const announcementId = currentAnnouncement.id;
        const freq = normalizeAnnouncementDisplayFrequency(currentAnnouncement.displayFrequency);
        const timeOnScreen = viewStartTime.current ? Date.now() - viewStartTime.current : 0;
        const testVariant = variant;
        const splash = isProductSplashAnnouncement(currentAnnouncement);

        if (splash) {
            if (freq === 'always' || freq === 'daily') {
                markAnnouncementDismissed(announcementId, freq);
            }
            if (freq !== 'always') {
                await persistProductSplashSeen(currentAnnouncement);
            }
        } else {
            markAnnouncementDismissed(announcementId, freq);
        }

        setIsVisible(false);
        setTimeout(() => {
            setCurrentAnnouncement(null);
            shownPopupIdRef.current = null;
        }, 300);

        trackAnnouncementDismissal(announcementId, userData?.uid, timeOnScreen, testVariant).catch(
            (err) => console.warn('[AnnouncementPopup] dismissal track failed', err)
        );
    };

    const handleCTAClick = async () => {
        const splash = isProductSplashAnnouncement(currentAnnouncement);
        const href = splash
            ? (currentAnnouncement.ctaLink || buildProductSplashCtaPath(currentAnnouncement.productSku))
            : currentAnnouncement?.ctaLink;
        if (!href) return;
        await trackAnnouncementClick(
            currentAnnouncement.id,
            userData?.uid,
            userData?.role,
            href,
            variant,
        );

        if (href.startsWith('/')) {
            navigate(href);
        } else {
            window.open(href, '_blank');
        }
        handleClose();
    };

    const handleAddToCart = async () => {
        if (!currentAnnouncement || addBusy) return;
        const sku = String(currentAnnouncement.productSku || '').trim();
        setAddBusy(true);
        try {
            const row = await loadShopProductRowForSplash(sku, customerPricingKey);
            const result = buildSplashAddToCartLine(row, customerPricingKey, 1);
            if (!result.ok) {
                window.alert(SPLASH_ADD_FAIL_COPY);
                return;
            }
            addItemsToCart([result.line]);
            await handleClose();
        } catch (error) {
            console.warn('[AnnouncementPopup] splash add to cart failed', error);
            window.alert(SPLASH_ADD_FAIL_COPY);
        } finally {
            setAddBusy(false);
        }
    };

    if (!currentAnnouncement) return null;

    const useVariantB = currentAnnouncement.abTesting?.enabled && variant === 'B';
    const variantData = useVariantB ? currentAnnouncement.abTesting.variantB : {};

    const displayTitle =
        useVariantB && variantData.title ? variantData.title : currentAnnouncement.title;
    const displayMessage =
        useVariantB && variantData.message ? variantData.message : currentAnnouncement.message;
    const displayCtaText =
        useVariantB && variantData.ctaText ? variantData.ctaText : currentAnnouncement.ctaText;

    return (
        <AnnouncementPopupCard
            announcement={currentAnnouncement}
            displayTitle={displayTitle}
            displayMessage={displayMessage}
            displayCtaText={displayCtaText}
            visible={isVisible}
            reducedMotion={reducedMotion}
            position={currentAnnouncement.position || 'center'}
            animation={currentAnnouncement.animation || 'fadeIn'}
            onClose={handleClose}
            onAddToCart={handleAddToCart}
            onViewProduct={handleCTAClick}
            onCta={handleCTAClick}
            addBusy={addBusy}
        />
    );
}
